import React from 'react';

import sortBy from 'lodash/sortBy';

import Media from 'src/components/media';
import SesamLink from 'Common/Links/SesamLink';
import { Card } from 'src/components/card/Card.view';
import InvoiceIcon from '../images/icons/invoice.svg';
import Button from 'Common/Button/Button';
import ButtonLink from 'src/components/common/ButtonLink/ButtonLink';

import { canEditPaymentMethod, canDeletePaymentMethod } from 'src/utils/paymentMethods.utils';
import { TestID } from 'src/testID';

import type { PaymentMethodCardProps } from 'src/types/common.types';
import type { PaymentMethodsState } from 'src/redux-store/reducers/payment-methods';
import type {
  PaymentMethodAPIPayload,
  PaymentMethodSubscription,
} from 'src/types/paymentMethod.types';

const CardText: React.FC<{
  pm: PaymentMethodAPIPayload;
}> = ({ pm }) => (
  <span>
    {pm?.subscriptions?.length > 0 && (
      <span>
        Used in{' '}
        {pm.subscriptions.map((subscription: PaymentMethodSubscription, idx: number) => (
          <span key={`${pm.id}-${subscription.id}`}>
            <SesamLink to={`/subscription/${subscription.id}`} key={subscription.id}>
              {subscription.name}
            </SesamLink>
            {idx < pm.subscriptions.length - 1 && ', '}
          </span>
        ))}
      </span>
    )}
    <br />
    {pm.description}
  </span>
);

export const usePaymentMethodCardsFactory = (
  paymentMethods: PaymentMethodsState,
  currentUserId: string,
  showRemoveConfirmation: (pm: PaymentMethodAPIPayload) => void
) => {
  const paymentMethodCards: PaymentMethodCardProps[] = sortBy(paymentMethods, 'name').map((pm) => {
    const text = <CardText pm={pm} />;

    const actions = [];

    if (canEditPaymentMethod(pm)) {
      actions.push(
        <ButtonLink
          key={crypto.randomUUID()}
          to={`/user/payment-methods/edit/${pm.id}`}
          size="small"
          theme="default"
          data-testid={TestID.PaymentMethodCardEditButton}
        >
          Edit…
        </ButtonLink>
      );
    }

    actions.push(
      <Button
        theme="secondary"
        disabled={!canDeletePaymentMethod(pm)}
        key={crypto.randomUUID()}
        onClick={() => showRemoveConfirmation(pm)}
        data-testid={TestID.PaymentMethodCardDeleteButton}
      >
        Delete
      </Button>
    );

    const header = (
      <Media img={<InvoiceIcon />} type="icon">
        <h3 className="heading-section">{`${pm.name} (${pm.type})`}</h3>
      </Media>
    );

    const badges = {
      verification: pm.is_verified ? 'Verified' : 'Not yet verified',
    };

    if (pm.owner && pm.owner.user_id && pm.owner.user_id !== currentUserId) {
      badges['other-owner-info'] = `Owned by '${pm.owner.email}')`;
    }

    return {
      actions,
      badges,
      renderer: Card,
      header,
      id: pm.id,
      text,
      type: pm.is_verified ? 'success' : 'default',
      testId: `${TestID.PaymentMethodCard}-${pm.id}`,
    };
  });

  return paymentMethodCards;
};
